/**
 * Auto Stacking Hooks
 * TanStack Query hooks for auto stacking settings
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
    getAutoStackingSettings,
    updateAutoStackingSettings,
    createAutoStackingTransactionFee,
    updateAutoStackingTransactionFee,
    deleteAutoStackingTransactionFee,
    bulkCreateAutoStackingTransactionFees,
} from '@/services/auto-stacking.service'
import type {
    AutoStackingSettings,
    UpdateAutoStackingSettingsRequest,
    AutoStackingTransactionFee,
    CreateAutoStackingTransactionFeeRequest,
    UpdateAutoStackingTransactionFeeRequest,
    BulkAutoStackingTransactionFeesRequest,
} from '@/types/auto-stacking'

export const useAutoStackingSettings = () => {
    return useQuery<AutoStackingSettings>({
        queryKey: ['auto-stacking-settings'],
        queryFn: getAutoStackingSettings,
        staleTime: 1000 * 60 * 2,
    })
}

export const useUpdateAutoStackingSettings = () => {
    const queryClient = useQueryClient()

    return useMutation<
        AutoStackingSettings,
        unknown,
        UpdateAutoStackingSettingsRequest
    >({
        mutationFn: data => updateAutoStackingSettings(data),
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: ['auto-stacking-settings'],
            })
        },
    })
}

export const useCreateAutoStackingTransactionFee = () => {
    const queryClient = useQueryClient()

    return useMutation<
        AutoStackingTransactionFee,
        unknown,
        CreateAutoStackingTransactionFeeRequest
    >({
        mutationFn: data => createAutoStackingTransactionFee(data),
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: ['auto-stacking-settings'],
            })
        },
    })
}

export const useUpdateAutoStackingTransactionFee = () => {
    const queryClient = useQueryClient()

    return useMutation<
        AutoStackingTransactionFee,
        unknown,
        { feeId: string; data: UpdateAutoStackingTransactionFeeRequest }
    >({
        mutationFn: ({ feeId, data }) =>
            updateAutoStackingTransactionFee(feeId, data),
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: ['auto-stacking-settings'],
            })
        },
    })
}

export const useDeleteAutoStackingTransactionFee = () => {
    const queryClient = useQueryClient()

    return useMutation<void, unknown, string>({
        mutationFn: feeId => deleteAutoStackingTransactionFee(feeId),
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: ['auto-stacking-settings'],
            })
        },
    })
}

export const useBulkCreateAutoStackingTransactionFees = () => {
    const queryClient = useQueryClient()

    return useMutation<
        AutoStackingTransactionFee[],
        unknown,
        BulkAutoStackingTransactionFeesRequest
    >({
        mutationFn: data => bulkCreateAutoStackingTransactionFees(data),
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: ['auto-stacking-settings'],
            })
        },
    })
}
